import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import ClientPicker from "@/components/ClientPicker";
import { toast } from "sonner";
import {
    Activity, AlertTriangle, ArrowUpCircle, Cpu, Filter, Layers,
    ListTree, Loader2, MemoryStick, RefreshCw, Search, Skull, X,
} from "lucide-react";

const STATUS_COLOURS = {
    queued: "text-amber-300 bg-amber-300/10 border-amber-300/30",
    running: "text-cyan-300 bg-cyan-300/10 border-cyan-300/30",
    done: "text-emerald-300 bg-emerald-300/10 border-emerald-300/30",
    dead: "text-rose-300 bg-rose-300/10 border-rose-300/30",
};

function fmtAgo(iso) {
    if (!iso) return "—";
    const dt = new Date(iso).getTime();
    if (!dt) return iso;
    const s = Math.max(0, Math.floor((Date.now() - dt) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

function StatusBadge({ status }) {
    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${STATUS_COLOURS[status] || "border border-white/10 text-white/50"
                }`}
            data-testid={`task-status-${status}`}
        >
            {status}
        </span>
    );
}

function PriorityPill({ p }) {
    const label = p === 0 ? "P0 boosted" : `P${p}`;
    const cls = p === 0
        ? "text-violet-300 bg-violet-300/10 border-violet-300/30"
        : p === 1 ? "text-white/80 border-white/20"
            : "text-white/50 border-white/10";
    return (
        <span className={`px-1.5 py-0.5 rounded border text-[10px] font-mono ${cls}`}>
            {label}
        </span>
    );
}

function StatChip({ icon: Icon, label, value, sub, status = "operational" }) {
    const cls = {
        operational: "border-white/10",
        degraded: "border-amber-300/40",
        down: "border-rose-300/40",
    }[status] || "border-white/10";
    return (
        <div className={`rounded-xl border ${cls} bg-white/[0.02] px-4 py-3`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/55">
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
            </div>
            <div className="mt-2 font-display text-2xl tabular-nums leading-none">{value ?? "—"}</div>
            {sub && <div className="text-[11px] text-white/40 font-mono mt-1">{sub}</div>}
        </div>
    );
}

export default function AdminTasksPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [kinds, setKinds] = useState([]);

    const [searchParams, setSearchParams] = useSearchParams();
    const [filter, setFilter] = useState({
        status: searchParams.get("status") || "",
        kind: searchParams.get("kind") || "",
        client_id: searchParams.get("client_id") || "",
        search: searchParams.get("search") || "",
    });
    useEffect(() => {
        const sp = new URLSearchParams();
        for (const [k, v] of Object.entries(filter)) {
            if (v && String(v).trim()) sp.set(k, String(v).trim());
        }
        setSearchParams(sp, { replace: true });
        // eslint-disable-next-line
    }, [filter]);
    const [busy, setBusy] = useState({}); // taskId -> action label

    const [boostTenantId, setBoostTenantId] = useState("");
    const [boostReason, setBoostReason] = useState("");
    const [boostBusy, setBoostBusy] = useState(false);

    const loadAll = async (opts = { showSpinner: true }) => {
        if (opts.showSpinner) setLoading(true);
        try {
            const params = new URLSearchParams();
            for (const [k, v] of Object.entries(filter)) {
                if (v && v.trim()) params.set(k, v.trim());
            }
            params.set("limit", "100");
            const [list, sum] = await Promise.all([
                api.get(`/admin/tasks?${params.toString()}`),
                api.get(`/admin/tasks/summary`),
            ]);
            setItems(list.data.items || []);
            setTotal(list.data.total || 0);
            setSummary(sum.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            if (opts.showSpinner) setLoading(false);
        }
    };

    const loadKinds = async () => {
        try {
            const { data } = await api.get(`/admin/tasks/kinds`);
            setKinds(data.kinds || []);
        } catch {
            /* non-fatal */
        }
    };

    useEffect(() => {
        loadKinds();
        loadAll();
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        loadAll();
        // eslint-disable-next-line
    }, [filter]);
    useEffect(() => {
        // 5-second silent poll while the tab is open
        const t = setInterval(() => loadAll({ showSpinner: false }), 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [filter]);

    const action = async (id, kind, label) => {
        setBusy((b) => ({ ...b, [id]: label }));
        try {
            await api.post(`/admin/tasks/${id}/${kind}`);
            toast.success(`${label} ✓`);
            await loadAll({ showSpinner: false });
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusy((b) => { const c = { ...b }; delete c[id]; return c; });
        }
    };

    const boostTenant = async () => {
        if (!boostTenantId.trim()) return toast.error("client_id required");
        setBoostBusy(true);
        try {
            const { data } = await api.post(`/admin/tasks/boost-tenant`, {
                client_id: boostTenantId.trim(),
                reason: boostReason.trim() || undefined,
            });
            toast.success(`Boosted ${data.boosted} task(s) for ${data.client_id}`);
            setBoostTenantId("");
            setBoostReason("");
            await loadAll({ showSpinner: false });
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBoostBusy(false);
        }
    };

    const tm = summary?.runtime || {};
    const cpu = tm.cpu_pct ?? 0;
    const ram = tm.ram_pct ?? 0;
    const cpuStatus = cpu > 80 ? "down" : cpu > 70 ? "degraded" : "operational";
    const ramStatus = ram > 80 ? "down" : ram > 70 ? "degraded" : "operational";

    return (
        <div className="px-8 py-8" data-testid="admin-tasks-page">
            <div className="flex items-start justify-between mb-6 gap-4">
                <div>
                    <div className="label-xs text-[#7C3AED]">Operations</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Task Queue</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Live view of the resource-aware task queue. Retry dead tasks, kill stuck
                        ones, and boost individual tasks or whole tenants to the front of the queue.
                        Pause-by-resource kicks in at 80% CPU or RAM.
                    </p>
                </div>
                <button
                    onClick={() => loadAll()}
                    disabled={loading}
                    data-testid="tasks-refresh-btn"
                    className="text-xs text-white/60 hover:text-white border border-white/10 rounded-full px-3 py-1.5 flex items-center gap-1.5"
                >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Refresh
                </button>
            </div>

            {/* Top stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <StatChip
                    icon={ListTree}
                    label="Queued"
                    value={summary?.queued ?? "—"}
                    sub={`${tm.global_concurrency ?? 32} global slots`}
                />
                <StatChip
                    icon={Activity}
                    label="Running"
                    value={summary?.running ?? "—"}
                    sub={`${tm.running_total ?? 0} in flight`}
                    status={tm.paused_by_resource ? "degraded" : "operational"}
                />
                <StatChip
                    icon={Skull}
                    label="Dead"
                    value={summary?.dead ?? "—"}
                    sub="exhausted retries"
                    status={(summary?.dead || 0) > 10 ? "degraded" : "operational"}
                />
                <StatChip
                    icon={Cpu}
                    label="CPU"
                    value={`${cpu}%`}
                    sub={tm.paused_by_resource ? "throttled" : "headroom ok"}
                    status={cpuStatus}
                />
                <StatChip
                    icon={MemoryStick}
                    label="RAM"
                    value={`${ram}%`}
                    sub={`${kinds.length} handler kinds`}
                    status={ramStatus}
                />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="flex items-center gap-1 text-xs text-white/40 mr-1">
                    <Filter className="w-3.5 h-3.5" /> Filter:
                </div>
                {["", "queued", "running", "done", "dead"].map((s) => (
                    <button
                        key={s || "all"}
                        onClick={() => setFilter((f) => ({ ...f, status: s }))}
                        data-testid={`tasks-filter-${s || "all"}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${filter.status === s
                            ? "bg-white text-black border-white"
                            : "border-white/10 text-white/60 hover:border-white/30"
                            }`}
                    >
                        {s || "all"}
                    </button>
                ))}
                <select
                    value={filter.kind}
                    onChange={(e) => setFilter((f) => ({ ...f, kind: e.target.value }))}
                    className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs ml-2"
                    data-testid="tasks-kind-filter"
                >
                    <option value="">all kinds</option>
                    {kinds.map((k) => (
                        <option key={k.kind} value={k.kind}>{k.kind} (cap {k.limit})</option>
                    ))}
                </select>
                <div className="relative ml-2">
                    <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        type="text"
                        placeholder="task id / correlation id"
                        value={filter.search}
                        onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
                        className="bg-white/5 border border-white/10 rounded-md pl-7 pr-2 py-1 text-xs w-64"
                        data-testid="tasks-search"
                    />
                </div>
                <ClientPicker
                    value={filter.client_id}
                    onChange={(v) => setFilter((f) => ({ ...f, client_id: v }))}
                    compact
                    width="w-56"
                    anyOptionLabel="all tenants"
                    testId="tasks-client-filter"
                />
            </div>

            {/* Tenant boost panel */}
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-6 flex flex-wrap items-end gap-3"
                data-testid="boost-tenant-panel">
                <div className="flex-1 min-w-[200px]">
                    <label className="label-xs block mb-1">Boost tenant priority</label>
                    <ClientPicker
                        value={boostTenantId}
                        onChange={setBoostTenantId}
                        compact
                        width="w-full"
                        placeholder="(select tenant…)"
                        testId="boost-tenant-input"
                        excludeDisabled
                    />
                </div>
                <div className="flex-[2] min-w-[260px]">
                    <label className="label-xs block mb-1">Reason (audit)</label>
                    <input
                        type="text"
                        placeholder="why this tenant gets P0 — e.g. 'paid-tier customer escalation'"
                        value={boostReason}
                        onChange={(e) => setBoostReason(e.target.value)}
                        className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm"
                        data-testid="boost-reason-input"
                    />
                </div>
                <button
                    onClick={boostTenant}
                    disabled={boostBusy || !boostTenantId.trim()}
                    data-testid="boost-tenant-submit"
                    className="px-4 py-2 rounded-full bg-[#7C3AED] hover:bg-[#9667ee] text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                    {boostBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpCircle className="w-4 h-4" />}
                    Boost queued tasks
                </button>
            </div>

            {/* Table */}
            <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="tasks-table">
                {loading && items.length === 0 ? (
                    <div className="p-10 text-center text-white/40 flex flex-col items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Loading queue…
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-10 text-center text-white/40 flex flex-col items-center gap-2">
                        <Layers className="w-6 h-6 opacity-40" />
                        No tasks match your filters.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-white/[0.02] text-white/50">
                            <tr>
                                <th className="text-left px-4 py-3 font-normal">Status</th>
                                <th className="text-left px-4 py-3 font-normal">Kind</th>
                                <th className="text-left px-4 py-3 font-normal">Priority</th>
                                <th className="text-left px-4 py-3 font-normal">Tenant</th>
                                <th className="text-left px-4 py-3 font-normal">Created</th>
                                <th className="text-left px-4 py-3 font-normal">Updated</th>
                                <th className="text-left px-4 py-3 font-normal">Attempts</th>
                                <th className="text-left px-4 py-3 font-normal">Detail</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((t) => (
                                <tr key={t.id} data-testid={`task-row-${t.id}`}
                                    className="border-t border-white/5 hover:bg-white/[0.02]">
                                    <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
                                    <td className="px-4 py-2 font-mono text-white/80">{t.kind}</td>
                                    <td className="px-4 py-2"><PriorityPill p={t.priority ?? 2} /></td>
                                    <td className="px-4 py-2 text-white/70 font-mono">{t.client_id || "—"}</td>
                                    <td className="px-4 py-2 text-white/50 text-xs">{fmtAgo(t.created_at)}</td>
                                    <td className="px-4 py-2 text-white/50 text-xs">{fmtAgo(t.updated_at)}</td>
                                    <td className="px-4 py-2 text-white/60 text-xs">
                                        {t.attempts ?? 0}/{t.max_attempts ?? 5}
                                    </td>
                                    <td className="px-4 py-2 text-white/60 text-xs max-w-[260px] truncate" title={t.last_error || ""}>
                                        {t.last_error
                                            ? <span className="text-rose-300/80 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{t.last_error.slice(0, 80)}</span>
                                            : t.correlation_id
                                                ? <span className="font-mono text-white/40">{t.correlation_id.slice(0, 8)}…</span>
                                                : "—"}
                                    </td>
                                    <td className="px-4 py-2 text-right whitespace-nowrap">
                                        {(t.status === "queued" || t.status === "running") && (
                                            <button
                                                onClick={() => action(t.id, "boost", "Boost")}
                                                disabled={busy[t.id] || t.priority === 0}
                                                data-testid={`task-boost-${t.id}`}
                                                title="Set priority=0 (top of queue)"
                                                className="text-[#a17bff] hover:text-white text-xs px-1.5 mr-1 disabled:opacity-30"
                                            >
                                                {busy[t.id] === "Boost" ? "…" : "Boost"}
                                            </button>
                                        )}
                                        {t.status === "dead" && (
                                            <button
                                                onClick={() => action(t.id, "retry", "Retry")}
                                                disabled={!!busy[t.id]}
                                                data-testid={`task-retry-${t.id}`}
                                                className="text-emerald-300 hover:text-white text-xs px-1.5 mr-1 disabled:opacity-30"
                                            >
                                                {busy[t.id] === "Retry" ? "…" : "Retry"}
                                            </button>
                                        )}
                                        {(t.status === "queued" || t.status === "running") && (
                                            <button
                                                onClick={() => action(t.id, "kill", "Kill")}
                                                disabled={!!busy[t.id]}
                                                data-testid={`task-kill-${t.id}`}
                                                title="Force terminal state. In-flight coroutines aren't cancelled — they just won't update the queue doc on completion."
                                                className="text-rose-300 hover:text-white text-xs px-1.5 disabled:opacity-30"
                                            >
                                                {busy[t.id] === "Kill" ? "…" : "Kill"}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <div className="text-[11px] text-white/30 font-mono mt-3" data-testid="tasks-total">
                Showing {items.length} of {total}. Auto-refreshes every 5s.
            </div>
        </div>
    );
}
